import { LazyMotion } from 'motion/react';
import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
} from 'react';
import { HashRouter, Navigate, Route, Routes, useLocation, type Location } from 'react-router-dom';

import { afterSettle } from '@/afterSettle';
import { useSeedBadges } from '@/awards';
import { useAuth } from '@/auth';
import { useFlushAttempts } from '@/hooks';
import { loadMotion, wantMotion } from '@/motionLoad';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { loadSheet, loadZoom } from '@/components/lazyUi';
import { Dock, TabBar } from '@/components/TabBar';
import { BackProvider } from '@/back';
import { CoveredContext, PAY_PATH } from '@/covered';
import { PlanProvider } from '@/plan';
import { SHOW_PAYMENTS } from '@/pro';
import { TopBar } from '@/components/TopBar';
import { Loader } from '@/components/Loader';
import { GuestGate } from '@/components/GuestGate';
import { Skeleton } from '@/components/Skeleton';
import { Notice } from '@/components/Notice';
import { OfflineBar } from '@/components/OfflineBar';
import { BiletlarPage } from '@/pages/BiletlarPage';
import { PracticePage } from '@/pages/PracticePage';
import { TodayPage } from '@/pages/TodayPage';
import { SavedProvider } from '@/saved';
import { ProfileNameProvider } from '@/profileName';
import { SavedSignsProvider } from '@/savedSigns';
import { useT, warmStrings } from '@/str';
import { TextSizeFrame, TextSizeProvider } from '@/textSize';

const screenLoaders: (() => Promise<unknown>)[] = [];

function screen<M, K extends keyof M>(load: () => Promise<M>, name: K, warm = true) {
  if (warm) screenLoaders.push(load);
  return lazy(() => load().then((m) => ({ default: m[name] as ComponentType })));
}

const ExamPage = screen(() => import('@/pages/ExamPage'), 'ExamPage');
const SavollarPage = screen(() => import('@/pages/SavollarPage'), 'SavollarPage');
const KutubxonaPage = screen(() => import('@/pages/KutubxonaPage'), 'KutubxonaPage');
const MavzularPage = screen(() => import('@/pages/MavzularPage'), 'MavzularPage');
const MavzuPage = screen(() => import('@/pages/MavzuPage'), 'MavzuPage');
const BelgilarPage = screen(() => import('@/pages/BelgilarPage'), 'BelgilarPage');
const BelgiPage = screen(() => import('@/pages/BelgiPage'), 'BelgiPage');
const BankPage = screen(() => import('@/pages/BankPage'), 'BankPage');
const SavedPage = screen(() => import('@/pages/SavedPage'), 'SavedPage');
const SettingsPage = screen(() => import('@/pages/SettingsPage'), 'SettingsPage');
const HistoryPage = screen(() => import('@/pages/HistoryPage'), 'HistoryPage');
const MarkazlarPage = screen(() => import('@/pages/MarkazlarPage'), 'MarkazlarPage');
const QoidalarPage = screen(() => import('@/pages/QoidalarPage'), 'QoidalarPage');
const QoidaPage = screen(() => import('@/pages/QoidalarPage'), 'QoidaPage');
const ShpargalkalarPage = screen(() => import('@/pages/ShpargalkalarPage'), 'ShpargalkalarPage');
const ShpargalkaPage = screen(() => import('@/pages/ShpargalkalarPage'), 'ShpargalkaPage');
const AvtodromPage = screen(() => import('@/pages/AvtodromPage'), 'AvtodromPage');
const MashqlarPage = screen(() => import('@/pages/AvtodromPage'), 'MashqlarPage');
const JarimalarPage = screen(() => import('@/pages/AvtodromPage'), 'JarimalarPage');
const TariflarPage = screen(() => import('@/pages/pay'), 'TariflarPage', SHOW_PAYMENTS);
const TolovPage = screen(() => import('@/pages/pay'), 'TolovPage', SHOW_PAYMENTS);
const KartaPage = screen(() => import('@/pages/pay'), 'KartaPage', SHOW_PAYMENTS);
const TekshirishPage = screen(() => import('@/pages/pay'), 'TekshirishPage', SHOW_PAYMENTS);
const NatijaPage = screen(() => import('@/pages/pay'), 'NatijaPage', SHOW_PAYMENTS);
const TolovlarPage = screen(() => import('@/pages/pay'), 'TolovlarPage', SHOW_PAYMENTS);
const QaytaAloqaPage = screen(() => import('@/pages/pay'), 'QaytaAloqaPage', SHOW_PAYMENTS);

interface NetworkHints {
  saveData?: boolean;
}

async function warmOneByOne(): Promise<void> {
  await loadSheet();
  await loadZoom();
  for (const load of screenLoaders) await load().catch(() => {});
  warmStrings();
}

function useWarmScreens(): void {
  useEffect(() => {
    const net = (navigator as Navigator & { connection?: NetworkHints }).connection;
    if (net?.saveData) return;
    return afterSettle(() => {
      wantMotion();
      void warmOneByOne();
    });
  }, []);
}

function ScreenWarmer() {
  useWarmScreens();
  return null;
}

function BadgeSeed() {
  useSeedBadges();
  return null;
}

function AttemptOutbox() {
  useFlushAttempts();
  return null;
}

const PAY_ROUTES = (
  <>
    <Route path="/tariflar" element={<TariflarPage />} />
    <Route path="/tolov" element={<TolovPage />} />
    <Route path="/tolov/karta" element={<KartaPage />} />
    <Route path="/tolov/tekshirish" element={<TekshirishPage />} />
    <Route path="/tolov/natija" element={<NatijaPage />} />
    <Route path="/tolovlar" element={<TolovlarPage />} />
    <Route path="/qayta-aloqa" element={<QaytaAloqaPage />} />
  </>
);

const OVERLAY = 'bg-background fixed inset-0 z-[55] flex flex-col justify-center';

function Screens() {
  const location = useLocation();
  const pay = SHOW_PAYMENTS && PAY_PATH.test(location.pathname);
  const [under, setUnder] = useState<Location | null>(null);
  if (!pay && under !== location) setUnder(location);
  const bg = pay ? under : null;
  const shown = bg ?? location;
  const covered = bg !== null;
  const opener = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (covered) {
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
      return;
    }
    const back = opener.current;
    opener.current = null;
    if (back && document.contains(back)) back.focus({ preventScroll: true });
  }, [covered]);

  return (
    <>
      <CoveredContext.Provider value={covered}>
        <div inert={covered}>
          <Suspense fallback={<Loader />}>
            <ErrorBoundary key={shown.pathname}>
              <Routes location={shown}>
                <Route path="/" element={<TodayPage />} />
                <Route path="/savollar" element={<SavollarPage />} />
                <Route path="/biletlar" element={<BiletlarPage />} />
                <Route path="/mavzular" element={<MavzularPage />} />
                <Route path="/mavzular/:topic" element={<MavzuPage />} />
                <Route path="/belgilar" element={<BelgilarPage />} />
                <Route path="/belgilar/:code" element={<BelgiPage />} />
                <Route path="/practice" element={<PracticePage />} />
                <Route
                  path="/practice/wrong"
                  element={
                    <GuestGate>
                      <PracticePage onlyWrong />
                    </GuestGate>
                  }
                />
                <Route
                  path="/practice/all"
                  element={
                    <GuestGate>
                      <BankPage />
                    </GuestGate>
                  }
                />
                <Route path="/practice/davom" element={<PracticePage resume />} />
                <Route path="/practice/tricky" element={<PracticePage filter="tricky" />} />
                <Route path="/practice/numeric" element={<PracticePage filter="numeric" />} />
                <Route path="/practice/random/:count" element={<PracticePage />} />
                <Route path="/practice/topic/:topic" element={<PracticePage />} />
                <Route path="/practice/lesson/:lesson" element={<PracticePage />} />
                <Route path="/practice/bilet/:number" element={<PracticePage />} />
                <Route path="/exam" element={<ExamPage />} />
                <Route path="/exam/:id" element={<ExamPage />} />
                <Route
                  path="/saved"
                  element={
                    <GuestGate>
                      <SavedPage />
                    </GuestGate>
                  }
                />
                <Route path="/kutubxona" element={<KutubxonaPage />} />
                <Route path="/shpargalkalar" element={<ShpargalkalarPage />} />
                <Route path="/shpargalkalar/:position" element={<ShpargalkaPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/qoidalar" element={<QoidalarPage />} />
                <Route path="/qoidalar/:position" element={<QoidaPage />} />
                <Route path="/markazlar" element={<MarkazlarPage />} />
                <Route path="/avtodrom" element={<AvtodromPage />} />
                <Route path="/avtodrom/mashqlar" element={<MashqlarPage />} />
                <Route path="/avtodrom/jarimalar" element={<JarimalarPage />} />
                <Route
                  path="/history"
                  element={
                    <GuestGate>
                      <HistoryPage />
                    </GuestGate>
                  }
                />
                {SHOW_PAYMENTS && PAY_ROUTES}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ErrorBoundary>
          </Suspense>
        </div>
      </CoveredContext.Provider>
      {bg && (
        <Suspense
          fallback={
            <div className={OVERLAY}>
              <Loader />
            </div>
          }
        >
          <ErrorBoundary key={location.pathname} className={OVERLAY}>
            <Routes>{PAY_ROUTES}</Routes>
          </ErrorBoundary>
        </Suspense>
      )}
    </>
  );
}

export function App() {
  const auth = useAuth();
  const t = useT();

  if (auth.status === 'loading') {
    return (
      <HashRouter>
        <TopBar />
        <Skeleton shape="rows" count={7} />
      </HashRouter>
    );
  }

  if (auth.status === 'error') {
    return <Notice sign="!" title={t.authFail} body={t.authFailBody} onRetry={auth.retry} />;
  }

  return (
    <LazyMotion key={auth.status} features={loadMotion} strict>
      <SavedProvider>
        <PlanProvider>
          <SavedSignsProvider>
            <ProfileNameProvider>
              <TextSizeProvider>
                <HashRouter>
                  <BackProvider>
                    <BadgeSeed />
                    <AttemptOutbox />
                    <ScreenWarmer />
                    <TopBar />
                    <OfflineBar />
                    <TextSizeFrame>
                      <Screens />
                    </TextSizeFrame>
                    <Dock>
                      <TabBar />
                    </Dock>
                  </BackProvider>
                </HashRouter>
              </TextSizeProvider>
            </ProfileNameProvider>
          </SavedSignsProvider>
        </PlanProvider>
      </SavedProvider>
    </LazyMotion>
  );
}
